import { Checkbox, Radio } from "antd";
import { Form, Formik } from "formik";
import React, { useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as Yup from "yup";
import { TRESO_ELEMENTS } from "../../../../data/constants";
import {
  exportWritingList,
  resetExportStatus,
} from "../../../../data/slices/exports";
import { closeModal } from "../../../../data/slices/modals";
import { getAccountBadgeStyles } from "../../../../utilities/colorUtils";
import Button from "../../../Button";
import Overlay from "../../../Overlay";
import HeaderOverlay from "../../../Project/Header";
import Title from "../../../Project/Title";
import "./index.scss";
import {
  processAccountTresoFromObject,
  transformFilterValues,
} from "../../../../hooks/useFiltersAndDate";
function ExportWritingFilter({ open, onCancel, onContinue, data, ...rest }) {
  const dispatch = useDispatch();

  const { exportStatus, exportError } = useSelector((state) => state.exports);

  const ExportSchema = Yup.object().shape({
    filterType: Yup.string().required(
      "Veuillez sélectionner un type de filtre"
    ),
    writingTypeFilters: Yup.array().when("filterType", {
      is: "writing_type",
      then: (schema) =>
        schema.min(1, "Veuillez sélectionner au moins un type d'écriture"),
      otherwise: (schema) => schema.notRequired(),
    }),
    selectedAccounts: Yup.object().when("filterType", {
      is: "treasury_accounts",
      then: (schema) =>
        schema.test(
          "has-accounts",
          "Veuillez sélectionner au moins un compte",
          (value) => Object.keys(value || {}).length > 0
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const { accounts } = useSelector((state) => state.accounts);

  // Initial form values
  const initialValues = useMemo(
    () => ({
      filterType: "none", // "none", "writing_type", "treasury_accounts"
      writingTypeFilters: [], // For Réelle/simul filters
      selectedAccounts: {}, // For treasury account filters
      allFiltersChecked: false,
      allPointedChecked: false,
    }),
    []
  );

  // Writing type filter data (from ExportSynthesisResult)
  const writingTypeFilterData = [
    {
      id: 2,
      label: "Réelle",
      children: [
        {
          id: 21,
          label: "soldée",
          background: "#a9efe5",
          filterValues: { status: "pointé", type: "réel" },
        },
        {
          id: 22,
          label: "non-soldée",
          background: "#e6fbf8",
          filterValues: { status: "engagé,prévisionnel", type: "réel" },
        },
      ],
    },
    {
      id: 3,
      label: "simul",
      filterValues: { type: "simulation" },
    },
  ];

  // Helper functions for writing type filters
  const handleWritingTypeFilterChange = (
    filter,
    checked,
    selectedFilters,
    setFieldValue
  ) => {
    const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

    const updatedFilters = checked
      ? [...selectedFilters, filter.filterValues]
      : selectedFilters.filter(
          (selected) => !isEqual(selected, filter.filterValues)
        );

    setFieldValue("writingTypeFilters", updatedFilters);
  };

  const handleWritingTypeParentFilterChange = (
    parent,
    checked,
    selectedFilters,
    setFieldValue
  ) => {
    const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

    const childValues = parent.children
      ? parent.children.map((child) => child.filterValues)
      : [];

    if (checked) {
      const filtersToAdd = [parent.filterValues, ...childValues].filter(
        Boolean
      );
      const newFilters = [...selectedFilters];

      filtersToAdd.forEach((filter) => {
        if (!newFilters.some((existing) => isEqual(existing, filter))) {
          newFilters.push(filter);
        }
      });

      setFieldValue("writingTypeFilters", newFilters);
    } else {
      const updatedFilters = selectedFilters.filter(
        (selected) =>
          !isEqual(selected, parent.filterValues) &&
          !childValues.some((childValue) => isEqual(selected, childValue))
      );

      setFieldValue("writingTypeFilters", updatedFilters);
    }
  };

  const isWritingTypeParentChecked = (parent, selectedFilters) => {
    const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
    const { children } = parent;
    if (!children || children.length === 0) return false;

    return children.every((child) =>
      selectedFilters.some((selected) => isEqual(selected, child.filterValues))
    );
  };

  // Helper functions for treasury account filters
  const checkIfAllFiltersAreSelected = (selectedAccounts) => {
    if (!accounts?.length) return false;

    return accounts.every((account) => {
      const accountStatuses = selectedAccounts[account.id] || [];
      const requiredStatuses = [
        ...TRESO_ELEMENTS.map((child) => child.status),
        "réel",
      ];
      return requiredStatuses.every((status) =>
        accountStatuses.includes(status)
      );
    });
  };

  const checkIfAllPointedAreSelected = (selectedAccounts) => {
    if (!accounts?.length) return false;

    return accounts.every((account) => {
      const accountStatuses = selectedAccounts[account.id] || [];
      return (
        accountStatuses.length === 2 &&
        accountStatuses.includes("pointé") &&
        accountStatuses.includes("réel")
      );
    });
  };

  const handleCheckAllFilters = (checked, setFieldValue) => {
    if (checked) {
      const updatedAccounts = {};
      accounts?.forEach((account) => {
        updatedAccounts[account.id] = [
          ...TRESO_ELEMENTS.map((child) => child.status),
          "réel",
        ];
      });
      setFieldValue("selectedAccounts", updatedAccounts);
    } else {
      setFieldValue("selectedAccounts", {});
    }
    setFieldValue("allFiltersChecked", checked);
  };

  const handleCheckAllPointed = (checked, setFieldValue) => {
    if (checked) {
      const updatedAccounts = {};
      accounts?.forEach((account) => {
        updatedAccounts[account.id] = ["pointé", "réel"];
      });
      setFieldValue("selectedAccounts", updatedAccounts);
      setFieldValue("allFiltersChecked", false);
    } else {
      setFieldValue("selectedAccounts", {});
    }
    setFieldValue("allPointedChecked", checked);
  };

  const handleParentChange = (
    accountTresoId,
    checked,
    setFieldValue,
    values
  ) => {
    const updatedAccounts = { ...values.selectedAccounts };

    if (checked) {
      updatedAccounts[accountTresoId] = TRESO_ELEMENTS.map(
        (child) => child.status
      );
      updatedAccounts[accountTresoId].push("réel");
    } else {
      delete updatedAccounts[accountTresoId];
    }

    setFieldValue("selectedAccounts", updatedAccounts);
    setFieldValue(
      "allFiltersChecked",
      checkIfAllFiltersAreSelected(updatedAccounts)
    );
    setFieldValue(
      "allPointedChecked",
      checkIfAllPointedAreSelected(updatedAccounts)
    );
  };

  const handleChildChange = (
    accountTresoId,
    child,
    checked,
    setFieldValue,
    values
  ) => {
    const updatedAccounts = { ...values.selectedAccounts };

    if (checked) {
      updatedAccounts[accountTresoId] = updatedAccounts[accountTresoId]
        ? [...updatedAccounts[accountTresoId], child.status]
        : [child.status];
    } else {
      const filteredStatuses = updatedAccounts[accountTresoId]?.filter(
        (status) => status !== child.status
      );
      if (filteredStatuses?.length) {
        updatedAccounts[accountTresoId] = filteredStatuses;
      } else {
        delete updatedAccounts[accountTresoId];
      }
    }

    // Auto-add "réel" type when relevant statuses are selected
    const statusesToCheck = ["prévisionnel", "engagé", "pointé"];
    Object.keys(updatedAccounts).forEach((id) => {
      const accountStatuses = updatedAccounts[id];
      const hasRelevantStatus = accountStatuses.some((status) =>
        statusesToCheck.includes(status)
      );

      if (hasRelevantStatus && !accountStatuses.includes("réel")) {
        accountStatuses.push("réel");
      } else if (!hasRelevantStatus) {
        const filteredStatuses = accountStatuses.filter(
          (status) => status !== "réel"
        );
        if (filteredStatuses.length) {
          updatedAccounts[id] = filteredStatuses;
        } else {
          delete updatedAccounts[id];
        }
      }
    });

    setFieldValue("selectedAccounts", updatedAccounts);
    setFieldValue(
      "allFiltersChecked",
      checkIfAllFiltersAreSelected(updatedAccounts)
    );
    setFieldValue(
      "allPointedChecked",
      checkIfAllPointedAreSelected(updatedAccounts)
    );
  };

  const isChildChecked = (accountTresoId, childStatus, values) => {
    const accountStatuses = values.selectedAccounts[accountTresoId] || [];
    return accountStatuses.includes(childStatus);
  };

  const isParentSelected = (accountTresoId, values) => {
    return !!values.selectedAccounts[accountTresoId];
  };

  const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    try {
      // Combine export data from previous modal with filter data
      let filterOptions = [];
      if (values.filterType === "writing_type") {
        filterOptions = transformFilterValues(values.writingTypeFilters);
      } else if (values.filterType === "treasury_accounts") {
        filterOptions = processAccountTresoFromObject(values.selectedAccounts);
      }

      const exportData = {
        startDate: data.exportData.startDate,
        endDate: data.exportData.endDate,
        filterType: values.filterType,
        filterOptions,
      };

      // Call the export API
      await dispatch(
        exportWritingList({
          projectId: data?.projectId,
          tableType: data?.tableType || "all",
          mode: data.exportData?.mode || "all",
          exportData,
        })
      ).unwrap();

      // Reset form on success
      resetForm({ values: initialValues });

      // Close modal
      dispatch(closeModal("export-writing-filter-modal"));
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = (resetForm) => {
    // Reset form on cancel
    if (resetForm) {
      resetForm({ values: initialValues });
    }

    if (typeof onCancel === "function") {
      onCancel();
    }
  };

  // Reset export status when modal opens
  useEffect(() => {
    if (open) {
      dispatch(resetExportStatus());
    }
  }, [open, dispatch]);

  // Log success message when export completes
  useEffect(() => {
    if (exportStatus === "succeeded") {
      console.log("Export completed successfully");
    }
  }, [exportStatus]);

  return (
    <Overlay show={open} closable={true}>
      <HeaderOverlay showlogo>
        <Title isDelete subTitle={`Export d'une liste des écritures`} />

        <Formik
          initialValues={initialValues}
          validationSchema={ExportSchema}
          onSubmit={handleFormSubmit}
          validateOnChange={true}
          validateOnBlur={true}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            handleSubmit,
            resetForm,
          }) => {
            return (
              <Form
                className="export-writing-filter-form"
                onSubmit={handleSubmit}
              >
                <div className="export-writing-filter-content">
                  <div className="export-writing-filter-content-wrapper">
                    <div className="export-writing-filter-section">
                      <div className="section-wrapper-header">
                        <div className="section-title">
                          Choisissez un filtre au niveau des écritures:
                        </div>
                      </div>
                      <div className="section-content">
                        <div className="radio-options-container">
                          <Radio.Group
                            onChange={(e) => {
                              setFieldValue("filterType", e.target.value);
                              // Reset filters when switching types
                              if (e.target.value !== "writing_type") {
                                setFieldValue("writingTypeFilters", []);
                              }
                              if (e.target.value !== "treasury_accounts") {
                                setFieldValue("selectedAccounts", {});
                                setFieldValue("allFiltersChecked", false);
                                setFieldValue("allPointedChecked", false);
                              }
                            }}
                            value={values.filterType}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 20,
                            }}
                          >
                            <div className="radio-option-item">
                              <Radio value="none">Aucun filtre</Radio>
                            </div>

                            <div className="radio-option-item">
                              <Radio value="writing_type">
                                Appliquer un filtre sur le type d'écritures:
                              </Radio>

                              {values.filterType === "writing_type" && (
                                <div className="filter-checkboxes-container">
                                  {writingTypeFilterData.map((filter) => {
                                    const isEqual = (a, b) =>
                                      JSON.stringify(a) === JSON.stringify(b);
                                    const areAllChildrenChecked =
                                      isWritingTypeParentChecked(
                                        filter,
                                        values.writingTypeFilters
                                      );

                                    return (
                                      <div
                                        key={filter.id}
                                        className="filter-group"
                                      >
                                        <Checkbox
                                          checked={
                                            filter.children
                                              ? areAllChildrenChecked
                                              : values.writingTypeFilters.some(
                                                  (selected) =>
                                                    isEqual(
                                                      selected,
                                                      filter.filterValues
                                                    )
                                                )
                                          }
                                          onChange={(e) =>
                                            filter.children
                                              ? handleWritingTypeParentFilterChange(
                                                  filter,
                                                  e.target.checked,
                                                  values.writingTypeFilters,
                                                  setFieldValue
                                                )
                                              : handleWritingTypeFilterChange(
                                                  filter,
                                                  e.target.checked,
                                                  values.writingTypeFilters,
                                                  setFieldValue
                                                )
                                          }
                                        >
                                          {filter.label}
                                        </Checkbox>

                                        {filter.children && (
                                          <div className="filter-children">
                                            {filter.children.map((child) => (
                                              <Checkbox
                                                key={child.id}
                                                checked={values.writingTypeFilters.some(
                                                  (selected) =>
                                                    isEqual(
                                                      selected,
                                                      child.filterValues
                                                    )
                                                )}
                                                onChange={(e) =>
                                                  handleWritingTypeFilterChange(
                                                    child,
                                                    e.target.checked,
                                                    values.writingTypeFilters,
                                                    setFieldValue
                                                  )
                                                }
                                              >
                                                <div
                                                  className="filter-child-label"
                                                  style={{
                                                    background:
                                                      child.background,
                                                  }}
                                                >
                                                  {child.label}
                                                </div>
                                              </Checkbox>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            <div className="radio-option-item">
                              <Radio value="treasury_accounts">
                                Appliquer un filtre sur les comptes de
                                trésorerie
                              </Radio>

                              {values.filterType === "treasury_accounts" && (
                                <div className="filter-checkboxes-container">
                                  <div className="treasury-filter-header">
                                    <Checkbox
                                      className="checkbox-all-filters"
                                      checked={values.allFiltersChecked}
                                      onChange={(e) =>
                                        handleCheckAllFilters(
                                          e.target.checked,
                                          setFieldValue
                                        )
                                      }
                                    >
                                      Tout sélectionner
                                    </Checkbox>

                                    <Checkbox
                                      className="checkbox-all-filters"
                                      checked={values.allPointedChecked}
                                      onChange={(e) =>
                                        handleCheckAllPointed(
                                          e.target.checked,
                                          setFieldValue
                                        )
                                      }
                                    >
                                      Statut pointé (tous les comptes)
                                    </Checkbox>
                                  </div>

                                  {accounts?.map((account) => (
                                    <div
                                      key={account.id}
                                      className="options-group"
                                    >
                                      <Checkbox
                                        checked={isParentSelected(
                                          account.id,
                                          values
                                        )}
                                        onChange={(e) =>
                                          handleParentChange(
                                            account.id,
                                            e.target.checked,
                                            setFieldValue,
                                            values
                                          )
                                        }
                                      >
                                        {account?.name}
                                      </Checkbox>

                                      <div className="filter-children">
                                        {TRESO_ELEMENTS?.map((child) => (
                                          <Checkbox
                                            key={child.id}
                                            checked={isChildChecked(
                                              account.id,
                                              child.status,
                                              values
                                            )}
                                            onChange={(e) =>
                                              handleChildChange(
                                                account.id,
                                                child,
                                                e.target.checked,
                                                setFieldValue,
                                                values
                                              )
                                            }
                                          >
                                            <div
                                              className="elements-types"
                                              style={{
                                                background:
                                                  child?.backgroundColor,
                                              }}
                                            >
                                              <div>{child.label}</div>
                                              <div
                                                className="compte-displayer-filter"
                                                style={getAccountBadgeStyles(
                                                  account?.color
                                                )}
                                              >
                                                {account?.code}
                                              </div>
                                            </div>
                                          </Checkbox>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </Radio.Group>

                          {errors.filterType && touched.filterType && (
                            <div className="error-message">
                              {errors.filterType}
                            </div>
                          )}
                          {errors.writingTypeFilters &&
                            touched.writingTypeFilters && (
                              <div className="error-message">
                                {errors.writingTypeFilters}
                              </div>
                            )}
                          {errors.selectedAccounts &&
                            touched.selectedAccounts && (
                              <div className="error-message">
                                {errors.selectedAccounts}
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="verify-inputs-popup">
                    {exportError && (
                      <div className="export-error">{exportError}</div>
                    )}

                    <div className="action-buttons">
                      <Button
                        text={"Annuler"}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleCancel(resetForm);
                        }}
                        className={"action-button"}
                        htmlType="button"
                        disabled={isSubmitting || exportStatus === "loading"}
                      />
                      <Button
                        text={"Continuer"}
                        htmlType="submit"
                        className={"action-button agree-action-button"}
                        disabled={isSubmitting || exportStatus === "loading"}
                        loading={isSubmitting || exportStatus === "loading"}
                      />
                    </div>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </HeaderOverlay>
    </Overlay>
  );
}

export default ExportWritingFilter;
